import { useEffect, useState } from "react";
import { api, errText } from "../../../lib/api";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Skeleton } from "../../../components/ui/skeleton";
import { toast } from "sonner";
import { Search, RefreshCw, FileSpreadsheet, FileDown } from "lucide-react";
import { HealthCheck } from "./HealthCheck";
import { FolderTree } from "./FolderTree";
import { PagesTable, StaffSections, RoutesList } from "./SiteMapParts";
import { JobsServices } from "./JobsServices";

const SECTIONS = [["pages", "Site pages"], ["health", "Health check"], ["staff", "Staff panel"], ["routes", "Server addresses"], ["jobs", "Jobs, services & data"], ["tree", "Folders & files"]];
const has = (q, ...vals) => !q || vals.some((v) => (v || "").toLowerCase().includes(q));

const Stat = ({ n, label }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3"><p className="font-display text-2xl font-bold text-[#12234A] tabular-nums">{n}</p><p className="text-xs text-slate-500">{label}</p></div>
);

export function SiteMapTab() {
  const [d, setD] = useState(null);
  const [sec, setSec] = useState("pages");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const download = (format) => {
    setBusy(format);
    api.get("/admin/site-map/export", { params: { format }, responseType: "blob", timeout: 120000 })
      .then((r) => {
        const name = (r.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || `site-map.${format}`;
        const a = document.createElement("a");
        a.href = URL.createObjectURL(r.data);
        a.download = name;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      })
      .catch(() => toast.error("Couldn't create the file. Please try again."))
      .finally(() => setBusy(""));
  };
  const load = () => { setD(null); api.get("/admin/site-map").then((r) => setD(r.data)).catch((e) => toast.error(errText(e))); };
  useEffect(load, []);
  if (!d) return <div className="space-y-3"><Skeleton className="h-20 rounded-2xl" /><Skeleton className="h-96 rounded-2xl" /></div>;
  const s = q.trim().toLowerCase();
  const files = d.tree.reduce((n, t) => n + t.files, 0);
  const body = {
    pages: <PagesTable pages={d.pages.filter((p) => has(s, p.path, p.description, p.component, p.access))} />,
    health: <HealthCheck pages={d.pages} />,
    staff: <StaffSections items={d.staff_sections.filter((x) => has(s, x.label, x.area))} />,
    routes: <RoutesList routes={d.routes.filter((r) => has(s, r.path, r.name, r.area))} q={s} />,
    jobs: <JobsServices crons={d.crons.filter((c) => has(s, c.name, c.description))} services={d.services.filter((x) => has(s, x.name, x.purpose))} collections={d.collections.filter((c) => has(s, c.name))} />,
    tree: <FolderTree tree={d.tree} q={s} />,
  }[sec];
  return (
    <div className="space-y-5" data-testid="sitemap-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#12234A]">Site map & architecture</h2>
          <p className="text-sm text-slate-500">Built from the code{d.generated_at ? ` · ${d.source === "snapshot" ? "snapshot from" : "read"} ${new Date(d.generated_at).toLocaleString()}` : ""}. Admins only.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => download("xlsx")} disabled={!!busy} className="gap-1.5" data-testid="sitemap-export-xlsx"><FileSpreadsheet size={15} /> {busy === "xlsx" ? "Preparing…" : "Spreadsheet"}</Button>
          <Button variant="outline" onClick={() => download("pdf")} disabled={!!busy} className="gap-1.5" data-testid="sitemap-export-pdf"><FileDown size={15} /> {busy === "pdf" ? "Preparing…" : "PDF"}</Button>
          <Button variant="outline" onClick={load} className="gap-1.5" data-testid="sitemap-refresh"><RefreshCw size={15} /> Refresh</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="sitemap-stats">
        <Stat n={d.pages.length} label="Site pages" /><Stat n={d.staff_sections.length} label="Staff sections" /><Stat n={d.routes.length} label="Server addresses" />
        <Stat n={d.crons.length} label="Scheduled jobs" /><Stat n={d.collections.length} label="Data collections" /><Stat n={files} label="Code files" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {SECTIONS.map(([id, label]) => (
          <button key={id} onClick={() => setSec(id)} data-testid={`sitemap-sec-${id}`}
            className={`h-9 px-4 rounded-full text-sm font-semibold border transition-colors ${sec === id ? "bg-[#12234A] text-white border-[#12234A]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>{label}</button>
        ))}
        <div className="relative ml-auto w-full sm:w-64">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter this section…" className="pl-9" data-testid="sitemap-search" />
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-5">{body}</div>
    </div>
  );
}
